import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
    Grid,
    Typography,
    Button,
    Chip,
    CircularProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Divider,
    Box
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import WarningIcon from '@material-ui/icons/Warning';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import PersonIcon from '@material-ui/icons/Person';
import SecurityIcon from '@material-ui/icons/Security';

const useStyles = makeStyles((theme) => ({
    section: {
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(2),
    },
    sectionTitle: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: theme.spacing(2),
    },
    sectionIcon: {
        marginRight: theme.spacing(1),
        color: theme.palette.primary.main,
    },
    ownerCard: {
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2),
        border: '1px solid #e0e0e0',
        borderRadius: theme.spacing(1),
        backgroundColor: '#fafafa',
    },
    ownerHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing(1),
    },
    ownerName: {
        fontWeight: 500,
    },
    ownerInfo: {
        color: theme.palette.text.secondary,
        fontSize: '0.875rem',
    },
    chipNoMatch: {
        backgroundColor: '#4caf50',
        color: 'white',
    },
    chipPossibleMatch: {
        backgroundColor: '#f44336',
        color: 'white',
    },
    chipNotScreened: {
        backgroundColor: '#9e9e9e',
        color: 'white',
    },
    chipProcessing: {
        backgroundColor: '#ff9800',
        color: 'white',
    },
    hitItem: {
        backgroundColor: '#fff3e0',
        borderRadius: theme.spacing(1),
        marginBottom: theme.spacing(1),
        padding: theme.spacing(1),
    },
    hitSource: {
        fontWeight: 500,
        color: '#e65100',
    },
    hitDetails: {
        fontSize: '0.875rem',
        color: theme.palette.text.secondary,
    },
    screenButton: {
        marginLeft: theme.spacing(1),
    },
    screenAllButton: {
        marginTop: theme.spacing(2),
    },
    lastScreened: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(0.5),
    },
    entitySkipped: {
        fontStyle: 'italic',
        color: theme.palette.text.secondary,
    },
    accordion: {
        marginTop: theme.spacing(1),
    },
    errorAlert: {
        marginBottom: theme.spacing(2),
    }
}));

/**
 * AML Screening Component
 * Displays screening controls and results for Primary Owner and Beneficial Owners
 */
const AmlScreening = ({
    organization,
    amlState,
    screenPrimaryOwner,
    screenBeneficialOwner,
    isLoading,
    error
}) => {
    const classes = useStyles();

    // Extract owner data from organization
    const primaryOwner = organization?.primaryOwner;
    const beneficialOwners = organization?.beneficialOwners || [];

    // Get screening results from AML state
    const primaryOwnerResult = amlState?.primaryOwner;
    const beneficialOwnerResults = amlState?.beneficialOwners || {};

    /**
     * Render status chip based on screening result
     */
    const renderStatusChip = (result, isProcessing = false) => {
        if (isProcessing) {
            return (
                <Chip
                    size="small"
                    label="Processing..."
                    className={classes.chipProcessing}
                    icon={<CircularProgress size={14} color="inherit" />}
                />
            );
        }

        if (!result) {
            return (
                <Chip
                    size="small"
                    label="Not Screened"
                    className={classes.chipNotScreened}
                    icon={<HelpOutlineIcon />}
                />
            );
        }

        if (result.matchStatus === 'no_match') {
            return (
                <Chip
                    size="small"
                    label="No Match"
                    className={classes.chipNoMatch}
                    icon={<CheckCircleIcon />}
                />
            );
        }

        if (result.matchStatus === 'possible_match') {
            const hitCount = result.hits?.length || 0;
            return (
                <Chip
                    size="small"
                    label={`Possible Match (${hitCount})`}
                    className={classes.chipPossibleMatch}
                    icon={<WarningIcon />}
                />
            );
        }

        return (
            <Chip
                size="small"
                label={result.matchStatus || 'Unknown'}
                className={classes.chipNotScreened}
            />
        );
    };

    /**
     * Render hit details - handles Veriff API response structure
     */
    const renderHits = (hits) => {
        if (!hits || hits.length === 0) return null;

        // Helper to get all sources from listingsRelatedToMatch
        const getListingSources = (listings) => {
            if (!listings) return [];
            const sources = [];

            // Check each category
            const categories = [
                { key: 'pep', label: 'PEP' },
                { key: 'sanctions', label: 'Sanctions' },
                { key: 'adverseMedia', label: 'Adverse Media' },
                { key: 'warnings', label: 'Warnings' },
                { key: 'fitnessProbity', label: 'Fitness & Probity' }
            ];

            categories.forEach(({ key, label }) => {
                if (listings[key] && listings[key].length > 0) {
                    listings[key].forEach(item => {
                        sources.push({
                            category: label,
                            sourceName: item.sourceName || item.name || 'Unknown',
                            sourceUrl: item.sourceUrl || item.url || null
                        });
                    });
                }
            });

            return sources;
        };

        return (
            <Accordion className={classes.accordion}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="body2">
                        View {hits.length} match{hits.length > 1 ? 'es' : ''} details
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Box style={{ width: '100%' }}>
                        {hits.map((hit, index) => {
                            const sources = getListingSources(hit.listingsRelatedToMatch);

                            return (
                                <Box key={index} className={classes.hitItem} style={{ marginBottom: 12 }}>
                                    {/* Matched Name */}
                                    <Typography className={classes.hitSource} style={{ marginBottom: 8 }}>
                                        Matched: {hit.matchedName || 'Unknown'}
                                    </Typography>

                                    {/* Match Details */}
                                    <Typography className={classes.hitDetails} style={{ marginBottom: 4 }}>
                                        {hit.countries && hit.countries.length > 0 && (
                                            <span><strong>Countries:</strong> {hit.countries.join(', ')} | </span>
                                        )}
                                        {hit.matchTypes && hit.matchTypes.length > 0 && (
                                            <span><strong>Match Type:</strong> {hit.matchTypes.join(', ')}</span>
                                        )}
                                    </Typography>

                                    {/* AKA */}
                                    {hit.aka && hit.aka.length > 0 && (
                                        <Typography className={classes.hitDetails} style={{ marginBottom: 8 }}>
                                            <strong>Also Known As:</strong> {hit.aka.join(', ')}
                                        </Typography>
                                    )}

                                    {/* Listing Sources */}
                                    {sources.length > 0 ? (
                                        <Box style={{ marginTop: 8 }}>
                                            <Typography variant="body2" style={{ fontWeight: 500, marginBottom: 4 }}>
                                                Sources:
                                            </Typography>
                                            {sources.map((source, sIdx) => (
                                                <Box key={sIdx} style={{
                                                    backgroundColor: '#fff8e1',
                                                    padding: '8px 12px',
                                                    borderRadius: 4,
                                                    marginBottom: 4,
                                                    borderLeft: '3px solid #ff9800'
                                                }}>
                                                    <Chip
                                                        size="small"
                                                        label={source.category}
                                                        style={{
                                                            marginRight: 8,
                                                            backgroundColor: source.category === 'Sanctions' ? '#f44336' :
                                                                           source.category === 'PEP' ? '#ff9800' :
                                                                           source.category === 'Adverse Media' ? '#9c27b0' : '#2196f3',
                                                            color: 'white',
                                                            fontSize: '0.7rem'
                                                        }}
                                                    />
                                                    {source.sourceUrl ? (
                                                        <a
                                                            href={source.sourceUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            style={{ color: '#1976d2' }}
                                                        >
                                                            {source.sourceName}
                                                        </a>
                                                    ) : (
                                                        <span>{source.sourceName}</span>
                                                    )}
                                                </Box>
                                            ))}
                                        </Box>
                                    ) : (
                                        <Typography className={classes.hitDetails} style={{ fontStyle: 'italic' }}>
                                            No detailed source information available
                                        </Typography>
                                    )}
                                </Box>
                            );
                        })}
                    </Box>
                </AccordionDetails>
            </Accordion>
        );
    };

    /**
     * Handle primary owner screening
     */
    const handleScreenPrimaryOwner = () => {
        if (!primaryOwner?.firstName || !primaryOwner?.lastName) {
            alert('Primary owner must have first name and last name to screen');
            return;
        }

        screenPrimaryOwner({
            firstName: primaryOwner.firstName,
            lastName: primaryOwner.lastName,
            dateOfBirth: primaryOwner.dob || null
        });
    };

    /**
     * Handle beneficial owner screening
     */
    const handleScreenBeneficialOwner = (owner, index) => {
        if (!owner?.firstName || !owner?.lastName) {
            alert('Beneficial owner must have first name and last name to screen');
            return;
        }

        screenBeneficialOwner({
            firstName: owner.firstName,
            lastName: owner.lastName,
            dateOfBirth: owner.dob || null
        }, index);
    };

    /**
     * Screen all eligible individuals
     */
    const handleScreenAll = async () => {
        // Screen primary owner if eligible
        if (primaryOwner?.firstName && primaryOwner?.lastName && !primaryOwnerResult) {
            handleScreenPrimaryOwner();
        }

        // Screen beneficial owners if eligible (skip entities)
        beneficialOwners.forEach((owner, index) => {
            if (!owner.isEntity && owner.firstName && owner.lastName && !beneficialOwnerResults[index]) {
                handleScreenBeneficialOwner(owner, index);
            }
        });
    };

    // Count screenable individuals
    const screenableCount = (primaryOwner?.firstName && primaryOwner?.lastName ? 1 : 0) +
        beneficialOwners.filter(o => !o.isEntity && o.firstName && o.lastName).length;

    const screenedCount = (primaryOwnerResult ? 1 : 0) +
        Object.keys(beneficialOwnerResults).length;

    return (
        <div className={classes.section}>
            <div className={classes.sectionTitle}>
                <SecurityIcon className={classes.sectionIcon} />
                <Typography variant="h6">AML Screening</Typography>
            </div>

            {error && (
                <Alert severity="error" className={classes.errorAlert}>
                    {error}
                </Alert>
            )}

            {/* Primary Owner */}
            {primaryOwner && (
                <div className={classes.ownerCard}>
                    <div className={classes.ownerHeader}>
                        <div>
                            <Typography className={classes.ownerName}>
                                <PersonIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: 4 }} />
                                Primary Owner: {primaryOwner.firstName} {primaryOwner.lastName}
                            </Typography>
                            {primaryOwner.dob && (
                                <Typography className={classes.ownerInfo}>
                                    DOB: {primaryOwner.dob}
                                </Typography>
                            )}
                        </div>
                        <div>
                            {renderStatusChip(primaryOwnerResult, isLoading && !primaryOwnerResult)}
                            <Button
                                size="small"
                                variant="outlined"
                                color="primary"
                                className={classes.screenButton}
                                onClick={handleScreenPrimaryOwner}
                                disabled={isLoading || !primaryOwner.firstName || !primaryOwner.lastName}
                            >
                                {primaryOwnerResult ? 'Re-Screen' : 'Screen'}
                            </Button>
                        </div>
                    </div>
                    {primaryOwnerResult?.screenedAt && (
                        <Typography className={classes.lastScreened}>
                            Last screened: {new Date(primaryOwnerResult.screenedAt).toLocaleString()}
                        </Typography>
                    )}
                    {renderHits(primaryOwnerResult?.hits)}
                </div>
            )}

            {!primaryOwner && (
                <Typography color="textSecondary" style={{ marginBottom: 16 }}>
                    No primary owner information available
                </Typography>
            )}

            {/* Beneficial Owners */}
            {beneficialOwners.length > 0 && (
                <>
                    <Divider style={{ margin: '16px 0' }} />
                    <Typography variant="subtitle1" gutterBottom>
                        Beneficial Owners ({beneficialOwners.length})
                    </Typography>

                    {beneficialOwners.map((owner, index) => (
                        <div key={index} className={classes.ownerCard}>
                            {owner.isEntity ? (
                                <Typography className={classes.entitySkipped}>
                                    #{index + 1} {owner.entityName || owner.name || 'Entity'} - Entity (AML screening not applicable)
                                </Typography>
                            ) : (
                                <>
                                    <div className={classes.ownerHeader}>
                                        <div>
                                            <Typography className={classes.ownerName}>
                                                <PersonIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: 4 }} />
                                                #{index + 1}: {owner.firstName} {owner.lastName}
                                            </Typography>
                                            {owner.dob && (
                                                <Typography className={classes.ownerInfo}>
                                                    DOB: {owner.dob}
                                                </Typography>
                                            )}
                                        </div>
                                        <div>
                                            {renderStatusChip(beneficialOwnerResults[index], isLoading && !beneficialOwnerResults[index])}
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                color="primary"
                                                className={classes.screenButton}
                                                onClick={() => handleScreenBeneficialOwner(owner, index)}
                                                disabled={isLoading || !owner.firstName || !owner.lastName}
                                            >
                                                {beneficialOwnerResults[index] ? 'Re-Screen' : 'Screen'}
                                            </Button>
                                        </div>
                                    </div>
                                    {beneficialOwnerResults[index]?.screenedAt && (
                                        <Typography className={classes.lastScreened}>
                                            Last screened: {new Date(beneficialOwnerResults[index].screenedAt).toLocaleString()}
                                        </Typography>
                                    )}
                                    {renderHits(beneficialOwnerResults[index]?.hits)}
                                </>
                            )}
                        </div>
                    ))}
                </>
            )}

            {/* Screen All Button */}
            {screenableCount > 0 && (
                <Button
                    variant="contained"
                    color="primary"
                    className={classes.screenAllButton}
                    onClick={handleScreenAll}
                    disabled={isLoading || screenedCount >= screenableCount}
                    fullWidth
                >
                    {isLoading ? (
                        <>
                            <CircularProgress size={20} color="inherit" style={{ marginRight: 8 }} />
                            Screening...
                        </>
                    ) : (
                        `Screen All (${screenedCount}/${screenableCount} completed)`
                    )}
                </Button>
            )}
        </div>
    );
};

export default AmlScreening;
